import { Component, DestroyRef, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { of, tap } from 'rxjs';
import { DynamicRouteHandlingService, InputFieldComponent } from '@zayan-erp/shared';
import {
  TaxCategoriesService,
  TaxCategoryCreateDto,
  TaxCategoryDto,
  TaxCategoryUpdateDto
} from '../../services/tax-categories.service';

@Component({
  selector: 'lib-tax-categories-form',
  imports: [InputFieldComponent, ReactiveFormsModule],
  templateUrl: './tax-categories-form.component.html'
})
export class TaxCategoriesFormComponent {
  #taxCategoriesService = inject(TaxCategoriesService);
  #dynamicRouteHandling = inject(DynamicRouteHandlingService);
  #activatedRoute = inject(ActivatedRoute);
  #router = inject(Router);
  #destroyRef = inject(DestroyRef);

  selectedTaxCategoryId = signal<string | null>(this.#activatedRoute.snapshot.paramMap.get('id'));

  taxCategoryForm = new FormGroup({
    code: new FormControl<string | null>(null, Validators.required),
    description: new FormControl<string | null>(null)
  });

  constructor() {
    this.#dynamicRouteHandling.submitButton.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.submit();
    });
  }

  editRxResource = rxResourceFixed({
    params: () => ({ id: this.selectedTaxCategoryId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.#taxCategoriesService
          .getTaxCategoryById(params.id)
          .pipe(tap(taxCategory => this.editForm(taxCategory)));
      }
      return of(null);
    }
  });

  editForm(taxCategory: TaxCategoryDto) {
    this.taxCategoryForm.patchValue({
      code: taxCategory.code,
      description: taxCategory.description
    });
  }

  submit() {
    if (this.taxCategoryForm.invalid) {
      this.taxCategoryForm.markAllAsTouched();
      return;
    }

    const payload = this.taxCategoryForm.value as TaxCategoryCreateDto | TaxCategoryUpdateDto;

    if (this.selectedTaxCategoryId()) {
      this.#taxCategoriesService
        .updateTaxCategory(this.selectedTaxCategoryId()!, payload)
        .subscribe({ next: () => this.afterSubmit() });
    } else {
      this.#taxCategoriesService.createTaxCategory(payload).subscribe({ next: () => this.afterSubmit() });
    }
  }

  afterSubmit() {
    this.taxCategoryForm.reset();
    this.#router.navigate([this.#activatedRoute.snapshot.data['returnRoute']], {
      queryParamsHandling: 'preserve'
    });
    this.#dynamicRouteHandling.reload.next(!this.#dynamicRouteHandling.reload.value);
  }
}
